import type { SearchCriteria, WorkItemStatus } from "../../shared/types/api";
import { isClosedStatus, STATUS_ORDER } from "./workItemVocabulary";

/** Nothing chosen anywhere. Also what Reset returns to. */
export const BLANK: SearchCriteria = {
  q: "",
  projectIds: [],
  statuses: [],
  assigneeIds: [],
  unassigned: false,
  severities: [],
  priorities: [],
};

/** The sentinel the assignee filter uses for "nobody", since nobody has an id. */
export const UNASSIGNED = "__unassigned__";

/**
 * What a search covers when nobody narrowed the status.
 *
 * <p>Everything except finished work. Somebody searching is nearly always looking for something
 * to do something about, and a project with a year of delivered work behind it answers every
 * question with that history first — the eight rows that matter arrive under two hundred that
 * are closed.
 *
 * <p>Cancelled counts as finished. It only excluded Done at first, so cancelled work came back
 * under "Any open" and could be claimed — and a claim on cancelled work is invisible, because
 * every screen that lists what somebody has to do lists open work.
 *
 * <p>Both are left out rather than made unaskable: choosing either in the Status filter searches
 * it, which is how somebody finds the item they closed last week.
 */
export const OPEN_STATUSES: WorkItemStatus[] = STATUS_ORDER.filter(
  (status) => !isClosedStatus(status),
);

/**
 * Whether anything at all was asked.
 *
 * <p>An empty form is not a question. The server answers one with an empty page, and the Search
 * button stays disabled rather than letting somebody press it and read the empty result as "there
 * is no such work".
 */
export function isBlank(criteria: SearchCriteria): boolean {
  return (
    criteria.q.trim() === ""
    && criteria.projectIds.length === 0
    && criteria.statuses.length === 0
    && criteria.assigneeIds.length === 0
    && !criteria.unassigned
    && criteria.severities.length === 0
    && criteria.priorities.length === 0
  );
}

/**
 * The criteria as a query string.
 *
 * <p>Each choice is a repeat of the same parameter rather than one comma-joined value: it is what
 * Spring binds to a Set without a converter, and it means a project name containing a comma can
 * never split one filter into two.
 *
 * <p>A filter nobody used contributes no parameter at all, which is what makes an empty filter
 * mean "do not narrow on this" rather than "match nothing".
 *
 * <p>Status is the one exception: unasked, it is sent as the open statuses rather than left out
 * entirely, so finished work stays out of an answer nobody restricted. Sent as parameters rather
 * than left to the server so the count above the list is the count of what is listed.
 */
export function toParams(criteria: SearchCriteria, pageSize = 50): URLSearchParams {
  const params = new URLSearchParams({ size: String(pageSize) });
  if (criteria.q.trim()) {
    params.set("q", criteria.q.trim());
  }
  criteria.projectIds.forEach((id) => params.append("projectId", id));
  (criteria.statuses.length > 0 ? criteria.statuses : OPEN_STATUSES).forEach(
    (value) => params.append("status", value),
  );
  criteria.assigneeIds.forEach((id) => params.append("assigneeId", id));
  criteria.severities.forEach((value) => params.append("severity", value));
  criteria.priorities.forEach((value) => params.append("priority", value));
  if (criteria.unassigned) {
    params.set("unassigned", "true");
  }
  return params;
}
